import "./index.css";

import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { createRouter, RouterProvider } from "@tanstack/react-router";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { StytchProvider } from "@stytch/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { loadStytch } from "@/lib/stytch.ts";
import { AuthProvider, useAuth } from "@/components/auth/auth-context.tsx";
import { LoadingScreen } from "@/components/loading-screen.tsx";
import * as Sentry from "@sentry/react";
import { usePostHogSetup } from "@/lib/posthog";
import { PostHogProviderWrapper } from "@/components/posthog";

if (
  !import.meta.env.VITE_IS_LOCAL ||
  import.meta.env.VITE_IS_LOCAL === "false"
) {
  //if we're not in the dev env, initialize PostHog and Sentry
  const env = import.meta.env.VITE_STYTCH_PUBLIC_ENV;

  if (import.meta.env.VITE_NODE_ENV !== "development") {
    Sentry.init({
      dsn: "https://504ae48c2d48cebfd2699138daf053b8@o4504608833798144.ingest.us.sentry.io/4507825595219968",

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/somethings\.com/],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      environment: env, // development or production
      ignoreErrors: ["N+1 API Call"],
      beforeSend(event) {
        // Filter out non-harmful Sendbird errors
        if (
          event.exception?.values?.some((exception) => {
            const errorMessage = exception.value?.toLowerCase() || "";
            return (
              exception.type === "SendbirdError" ||
              (errorMessage.includes("sendbird") &&
                (errorMessage.includes("connection is required") ||
                  errorMessage.includes("websocket connection closed") ||
                  errorMessage.includes("websocket connection error") ||
                  errorMessage.includes("network error") ||
                  errorMessage.includes("disconnected from server") ||
                  errorMessage.includes("Too many requests")))
            );
          })
        ) {
          return null; // Don't send these errors to Sentry
        }
        return event;
      },
    });
  } else {
    console.log("Sentry is not enabled");
  }
}

const queryClient = new QueryClient();

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: {
    stytchSession: null,
    user: null,
    revokeSession: () => {},
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
const stytchClient = loadStytch();

const InnerApp = () => {
  const { session, user, isAuthenticating, revokeSession } = useAuth();

  usePostHogSetup(user);

  if (isAuthenticating) {
    return <LoadingScreen />;
  }

  return (
    <RouterProvider
      router={router}
      context={{
        stytchSession: session,
        user,
        revokeSession,
      }}
    />
  );
};

const App = () => {
  return (
    <PostHogProviderWrapper>
      <QueryClientProvider client={queryClient}>
        <StytchProvider stytch={stytchClient}>
          <AuthProvider>
            <InnerApp />
          </AuthProvider>
        </StytchProvider>
      </QueryClientProvider>
    </PostHogProviderWrapper>
  );
};

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
